import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Info, Truck, Video, HandHeart, AlertTriangle, GraduationCap, Wrench, Eye, Globe, MapPin, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import ImageUpload from '../components/ImageUpload';
import VideoUpload from '../components/VideoUpload';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../utils/api';

const CreateListingPage = () => {
  const { id } = useParams(); // For edit mode
  const navigate = useNavigate();
  const { user, loading: authLoading, api, isSubscribed } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState({ states: [], major_cities: [] });
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const [agreedToHandmade, setAgreedToHandmade] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    condition: '',
    location: user?.location || '',
    area: '',
    offers_delivery: false,
    images: [],
    before_images: [],
    after_images: [],
    is_restoration: false,
    video_url: null,
    video_thumbnail: null,
    // Workshop fields
    listing_type: 'item',
    workshop_format: '',
    workshop_date: '',
    workshop_duration: '',
    workshop_capacity: '',
    workshop_external_link: '',
    // Recurring workshop
    is_recurring: false,
    recurrence_frequency: 'weekly',
    recurrence_day_of_week: '',
    recurrence_end_date: ''
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login', { state: { from: '/create-listing' } });
      return;
    }
    
    if (!user.is_seller) {
      toast.error('Start your seller trial to create listings');
      navigate('/subscription');
      return;
    }
    
    if (!isSubscribed()) {
      toast.error('Your trial has ended. Please subscribe to create listings');
      navigate('/subscription');
      return;
    }
    
    fetchCategories();
    fetchLocations();
    if (id) {
      fetchListing();
    }
  }, [user, id, authLoading]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/locations`);
      setLocations(res.data);
    } catch (err) {
      console.error('Failed to fetch locations', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`);
      setCategories(res.data.categories);
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  };

  const fetchListing = async () => {
    try {
      const res = await api().get(`/listings/${id}`);
      if (res.data.seller_id !== user.id) {
        toast.error('Not authorized to edit this listing');
        navigate('/dashboard');
        return;
      }
      setFormData({
        title: res.data.title,
        description: res.data.description,
        price: res.data.price.toString(),
        category: res.data.category,
        condition: res.data.condition,
        location: res.data.location,
        area: res.data.area || '',
        offers_delivery: res.data.offers_delivery || false,
        images: res.data.images || [],
        before_images: res.data.before_images || [],
        after_images: res.data.after_images || [],
        is_restoration: res.data.is_restoration,
        video_url: res.data.video_url || null,
        video_thumbnail: res.data.video_thumbnail || null,
        listing_type: res.data.listing_type || 'item',
        workshop_format: res.data.workshop_format || '',
        workshop_date: res.data.workshop_date || '',
        workshop_duration: res.data.workshop_duration || '',
        workshop_capacity: res.data.workshop_capacity ? res.data.workshop_capacity.toString() : '',
        workshop_external_link: res.data.workshop_external_link || '',
        is_recurring: res.data.is_recurring || false,
        recurrence_frequency: res.data.recurrence_frequency || 'weekly',
        recurrence_day_of_week: res.data.recurrence_day_of_week !== null && res.data.recurrence_day_of_week !== undefined ? res.data.recurrence_day_of_week.toString() : '',
        recurrence_end_date: res.data.recurrence_end_date || ''
      });
      if (res.data.video_url) {
        setShowVideoUpload(true);
      }
    } catch (err) {
      console.error('Failed to fetch listing', err);
      toast.error('Failed to load listing');
      navigate('/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isWorkshopType = formData.listing_type === 'hands_on_workshop' || formData.listing_type === 'look_and_learn';
    
    // For workshops, condition is auto-set; for items it's required from the user
    const condition = isWorkshopType ? (formData.condition || 'excellent') : formData.condition;
    
    if (!formData.title || !formData.description || !formData.price || !formData.category || !condition || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    const allImages = [...formData.images, ...formData.before_images, ...formData.after_images];
    if (allImages.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    if (formData.is_restoration && (formData.before_images.length === 0 || formData.after_images.length === 0)) {
      toast.error('Restoration projects require both before and after images');
      return;
    }

    // Only require handmade agreement for new ITEM listings (not workshops)
    if (!id && !isWorkshopType && !agreedToHandmade) {
      toast.error('Please confirm this item is handmade or self-designed');
      return;
    }

    const isWorkshop = isWorkshopType;

    if (isWorkshop) {
      if (formData.listing_type === 'look_and_learn' && !formData.workshop_format) {
        toast.error('Please choose Online, In-Person, or Both for your Look & Learn');
        return;
      }
      if (formData.listing_type === 'hands_on_workshop' && !formData.workshop_format) {
        toast.error('Please choose Online, In-Person, or Both for your workshop');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        condition: condition,
        price: parseFloat(formData.price),
        workshop_capacity: formData.workshop_capacity ? parseInt(formData.workshop_capacity, 10) : null,
        recurrence_day_of_week: formData.is_recurring && formData.recurrence_day_of_week !== '' ? parseInt(formData.recurrence_day_of_week, 10) : null,
        recurrence_frequency: formData.is_recurring ? formData.recurrence_frequency : null,
        recurrence_end_date: formData.is_recurring && formData.recurrence_end_date ? formData.recurrence_end_date : null,
        is_recurring: !!(isWorkshopType && formData.is_recurring),
        // Clean up workshop fields when not a workshop
        ...(isWorkshop ? {} : {
          workshop_format: null,
          workshop_date: null,
          workshop_duration: null,
          workshop_capacity: null,
          workshop_external_link: null
        }),
      };

      if (id) {
        await api().put(`/listings/${id}`, payload);
        toast.success('Listing updated successfully');
      } else {
        await api().post('/listings', payload);
        toast.success('Listing created successfully');
      }
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to save listing', err);
      toast.error(err.response?.data?.detail || 'Failed to save listing');
    } finally {
      setLoading(false);
    }
  };

  const conditions = [
    { value: 'excellent', label: 'Excellent - Like new' },
    { value: 'good', label: 'Good - Minor wear' },
    { value: 'fair', label: 'Fair - Some wear' },
    { value: 'needs-work', label: 'Needs Work - For restoration' }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-safe">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-teal-600 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-stone-900 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
          {id ? 'Edit Listing' : 'Create New Listing'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Listing Type Selector */}
          <div className="bg-white border border-stone-200 rounded-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              What are you listing?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { value: 'item', label: 'Item for Sale', desc: 'Handmade or restored item', icon: HandHeart },
                { value: 'hands_on_workshop', label: 'Hands-on Workshop', desc: 'In-person, attendees make something', icon: Wrench },
                { value: 'look_and_learn', label: 'Look & Learn', desc: 'Demo or class — online or in-person', icon: Eye }
              ].map(opt => {
                const Icon = opt.icon;
                const active = formData.listing_type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, listing_type: opt.value, workshop_format: opt.value === 'item' ? '' : formData.workshop_format })}
                    className={`text-left p-4 rounded-sm border-2 transition-all ${active ? 'border-teal-500 bg-teal-50' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                    data-testid={`listing-type-${opt.value}`}
                  >
                    <Icon className={`h-5 w-5 mb-2 ${active ? 'text-teal-600' : 'text-stone-400'}`} />
                    <div className={`font-semibold text-sm ${active ? 'text-teal-700' : 'text-stone-900'}`}>{opt.label}</div>
                    <div className="text-xs text-stone-500 mt-1">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Workshop Details — shown only for workshop / look-and-learn */}
          {(formData.listing_type === 'hands_on_workshop' || formData.listing_type === 'look_and_learn') && (
            <div className="bg-white border border-stone-200 rounded-sm p-6 space-y-5">
              <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <GraduationCap className="h-5 w-5 text-teal-600" />
                Workshop Details
              </h2>

              {(formData.listing_type === 'look_and_learn' || formData.listing_type === 'hands_on_workshop') && (
                <div>
                  <Label>Format *</Label>
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    {[
                      { value: 'in_person', label: 'In-Person', icon: MapPin },
                      { value: 'online', label: 'Online', icon: Globe },
                      { value: 'both', label: 'Both', icon: GraduationCap }
                    ].map(opt => {
                      const Icon = opt.icon;
                      const active = formData.workshop_format === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, workshop_format: opt.value })}
                          className={`flex items-center justify-center gap-2 p-3 rounded-sm border-2 transition-all ${active ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-stone-200 hover:border-stone-300 text-stone-700'}`}
                          data-testid={`workshop-format-${opt.value}`}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="font-medium text-sm">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="workshop_date">Date & Time <span className="text-stone-400 text-xs">(optional — leave blank for "by appointment")</span></Label>
                  <Input
                    id="workshop_date"
                    type="datetime-local"
                    value={formData.workshop_date}
                    onChange={(e) => setFormData({ ...formData, workshop_date: e.target.value })}
                    className="mt-1"
                    data-testid="workshop-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="workshop_duration">Duration</Label>
                  <Input
                    id="workshop_duration"
                    value={formData.workshop_duration}
                    onChange={(e) => setFormData({ ...formData, workshop_duration: e.target.value })}
                    placeholder="e.g., 2 hours"
                    className="mt-1"
                    data-testid="workshop-duration-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="workshop_capacity">Number of Seats</Label>
                <Input
                  id="workshop_capacity"
                  type="number"
                  min="1"
                  value={formData.workshop_capacity}
                  onChange={(e) => setFormData({ ...formData, workshop_capacity: e.target.value })}
                  placeholder="e.g., 10"
                  className="mt-1 max-w-xs"
                  data-testid="workshop-capacity-input"
                />
              </div>

              {/* Recurring workshop */}
              <div className="border-t border-stone-200 pt-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="is_recurring"
                    checked={formData.is_recurring}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                    className="mt-1"
                    data-testid="is-recurring-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="is_recurring" className="text-stone-900 font-medium cursor-pointer">
                      This is a recurring workshop
                    </Label>
                    <p className="text-xs text-stone-500 mt-1">
                      Tick this if you run this workshop on a repeating schedule — no need to re-post every week.
                    </p>
                  </div>
                </div>

                {formData.is_recurring && (
                  <div className="mt-4 pl-8 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Frequency *</Label>
                        <Select
                          value={formData.recurrence_frequency}
                          onValueChange={(value) => setFormData({ ...formData, recurrence_frequency: value })}
                        >
                          <SelectTrigger className="mt-1" data-testid="recurrence-frequency-select">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="fortnightly">Fortnightly (every 2 weeks)</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Day of the Week</Label>
                        <Select
                          value={formData.recurrence_day_of_week}
                          onValueChange={(value) => setFormData({ ...formData, recurrence_day_of_week: value })}
                        >
                          <SelectTrigger className="mt-1" data-testid="recurrence-day-select">
                            <SelectValue placeholder="Select day" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Monday</SelectItem>
                            <SelectItem value="1">Tuesday</SelectItem>
                            <SelectItem value="2">Wednesday</SelectItem>
                            <SelectItem value="3">Thursday</SelectItem>
                            <SelectItem value="4">Friday</SelectItem>
                            <SelectItem value="5">Saturday</SelectItem>
                            <SelectItem value="6">Sunday</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="recurrence_end_date">End Date <span className="text-stone-400 text-xs">(optional — leave blank for ongoing)</span></Label>
                      <Input
                        id="recurrence_end_date"
                        type="date"
                        value={formData.recurrence_end_date}
                        onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                        className="mt-1 max-w-xs"
                        data-testid="recurrence-end-date-input"
                      />
                    </div>
                    <p className="text-xs text-teal-700 bg-teal-50 border border-teal-100 rounded-sm p-2">
                      💡 Tip: use the <strong>Date & Time</strong> field above as your <em>first upcoming session</em>. We&apos;ll automatically show buyers the next date based on your recurrence.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="workshop_external_link" className="flex items-center gap-1">
                  {formData.workshop_format === 'online' ? 'Meeting Link' : 'Booking / Info Link'} <span className="text-stone-400 text-xs">(optional)</span>
                </Label>
                <div className="relative mt-1">
                  <ExternalLink className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    id="workshop_external_link"
                    type="url"
                    value={formData.workshop_external_link}
                    onChange={(e) => setFormData({ ...formData, workshop_external_link: e.target.value })}
                    placeholder={formData.workshop_format === 'online' ? 'https://zoom.us/...' : 'https://your-booking-page.com'}
                    className="pl-9"
                    data-testid="workshop-link-input"
                  />
                </div>
                <p className="text-xs text-stone-500 mt-1">
                  Buyers will be redirected off-site. Crafters Market doesn't process workshop bookings — you handle attendees directly via messages and email.
                </p>
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className="bg-white border border-stone-200 rounded-sm p-6 space-y-6">
            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Basic Information
            </h2>

            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Restored Mid-Century Dresser"
                  className="mt-1"
                  data-testid="title-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your item in detail..."
                  rows={5}
                  className="mt-1"
                  data-testid="description-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">{(formData.listing_type === 'hands_on_workshop' || formData.listing_type === 'look_and_learn') ? 'Price per Seat ($AUD) *' : 'Price ($AUD) *'}</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    className="mt-1"
                    data-testid="price-input"
                  />
                </div>

                <div>
                  <Label>Location (Australia) *</Label>
                  <Select
                    value={formData.location}
                    onValueChange={(value) => setFormData({ ...formData, location: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="location-select">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.major_cities.map((city) => (
                        <SelectItem key={`${city.name}-${city.state}`} value={`${city.name}, ${city.state}`}>
                          {city.name}, {city.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Area/Suburb Field */}
              <div>
                <Label htmlFor="area">Area/Suburb (for pickup)</Label>
                <Input
                  id="area"
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                  placeholder="e.g., Northside, Bayside, Inner West"
                  className="mt-1"
                  data-testid="area-input"
                />
                <p className="text-xs text-stone-500 mt-1">Help buyers know how far they'd need to travel</p>
              </div>

              {/* Delivery/Shipping Toggle */}
              <div className="flex items-start gap-3 p-4 bg-stone-50 border border-stone-200 rounded-sm">
                <Switch
                  id="offers_delivery"
                  checked={formData.offers_delivery}
                  onCheckedChange={(checked) => setFormData({ ...formData, offers_delivery: checked })}
                  data-testid="delivery-toggle"
                />
                <div className="flex-1">
                  <Label htmlFor="offers_delivery" className="text-stone-900 font-medium cursor-pointer flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    I offer delivery or shipping for this item
                  </Label>
                  <p className="text-xs text-stone-500 mt-1">
                    Delivery/shipping costs to be arranged directly with the buyer (not through the website)
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.listing_type === 'item' && (
                  <div>
                    <Label>Condition *</Label>
                    <Select
                      value={formData.condition}
                      onValueChange={(value) => setFormData({ ...formData, condition: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="condition-select">
                        <SelectValue placeholder="Select condition" />
                      </SelectTrigger>
                      <SelectContent>
                        {conditions.map((cond) => (
                          <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="bg-white border border-stone-200 rounded-sm p-6 space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Photos
                </h2>
                <p className="text-sm text-stone-500 mt-1">Add up to 10 photos total</p>
              </div>
            </div>

            {/* Restoration Toggle - only for items */}
            {formData.listing_type === 'item' && (
              <div className="flex items-start gap-3 p-4 bg-teal-50 border border-teal-100 rounded-sm">
                <Checkbox
                  id="restoration"
                  checked={formData.is_restoration}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_restoration: checked })}
                  data-testid="restoration-checkbox"
                />
                <div>
                  <Label htmlFor="restoration" className="text-stone-900 font-medium cursor-pointer">
                    This is a restoration project
                  </Label>
                  <p className="text-sm text-stone-500 mt-1">
                    Showcase your skills with before & after photos
                  </p>
                </div>
              </div>
            )}

            {formData.is_restoration ? (
              <div className="space-y-6">
                <ImageUpload
                  images={formData.before_images}
                  setImages={(imgs) => setFormData({ ...formData, before_images: imgs })}
                  maxImages={5}
                  label="Before Photos"
                />
                <ImageUpload
                  images={formData.after_images}
                  setImages={(imgs) => setFormData({ ...formData, after_images: imgs })}
                  maxImages={5}
                  label="After Photos"
                />
              </div>
            ) : (
              <ImageUpload
                images={formData.images}
                setImages={(imgs) => setFormData({ ...formData, images: imgs })}
                maxImages={10}
                label="Item Photos"
              />
            )}
          </div>

          {/* Video Section */}
          <div className="bg-white border border-stone-200 rounded-sm p-6 space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <Video className="inline h-5 w-5 mr-2 text-teal-600" />
                  Video (Optional)
                </h2>
                <p className="text-sm text-stone-500 mt-1">Show buyers how you made this item</p>
              </div>
            </div>

            {!showVideoUpload ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowVideoUpload(true)}
                className="w-full border-dashed"
              >
                <Video className="h-4 w-4 mr-2" />
                Add a "How It's Made" Video
              </Button>
            ) : (
              <div className="space-y-4">
                <VideoUpload
                  onVideoUploaded={(url) => setFormData({ ...formData, video_url: url })}
                  onThumbnailUploaded={(url) => setFormData({ ...formData, video_thumbnail: url })}
                  existingVideo={formData.video_url}
                  existingThumbnail={formData.video_thumbnail}
                />
                {!formData.video_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowVideoUpload(false)}
                    className="text-stone-500"
                  >
                    Cancel video upload
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Handmade Agreement - Only show for new ITEM listings */}
          {!id && formData.listing_type === 'item' && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <HandHeart className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-stone-900 mb-2">Handmade & Original Items Only</h3>
                  <p className="text-stone-600 text-sm mb-4">
                    Crafters Market is a dedicated platform for <strong>handmade, handcrafted, restored, or self-designed items</strong>. 
                    We are not a general marketplace like Gumtree or Facebook Marketplace.
                  </p>
                  
                  <div className="bg-white/60 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2 text-amber-700 text-sm mb-2">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="font-medium">The following items are NOT permitted:</span>
                    </div>
                    <ul className="text-sm text-stone-600 space-y-1 ml-6">
                      <li>• Mass-produced items bought from retailers or wholesalers</li>
                      <li>• Second-hand items you didn't make or restore yourself</li>
                      <li>• Dropshipped or resold products</li>
                      <li>• Items not made, designed, or restored by you</li>
                    </ul>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="handmade-agreement"
                      checked={agreedToHandmade}
                      onCheckedChange={setAgreedToHandmade}
                      className="mt-0.5 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
                      data-testid="handmade-checkbox"
                    />
                    <Label htmlFor="handmade-agreement" className="text-sm text-stone-700 cursor-pointer leading-relaxed">
                      I confirm this item is <strong>handmade, handcrafted, restored, or designed by me</strong>. 
                      I understand that listings violating this policy will be removed and may result in account suspension.
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || (!id && formData.listing_type === 'item' && !agreedToHandmade)}
              className="flex-1 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide disabled:opacity-50"
              data-testid="submit-listing-btn"
            >
              {loading ? (
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                id ? 'Update Listing' : 'Create Listing'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateListingPage;
